/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';
import { MOMENT_FORMATS, formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import { Grid, Typography2 } from '@coursera/cds-core';
import { color } from '@coursera/coursera-ui';
import { SvgFlagSquareFilled } from '@coursera/coursera-ui/svg';

import ArrayToStringCell from 'bundles/enterprise-admin-users/components/ArrayToStringCell';
import { ActivityStatus } from 'bundles/enterprise-admin-users/types';
import { Button, PersonallyIdentifiableInfo } from 'bundles/enterprise-ui';

import _t from 'i18n!nls/enterprise-admin-users';

const buttonStyle = css`
  padding: 0;
  max-width: fit-content;
  font-weight: normal;
  display: block;
  text-align: left;
`;

export const renderDetailsButton =
  (onUserClick?: (index: number) => void) => (userIdentifier: string, row: $TSFixMe, rowIndex: number) => {
    return onUserClick ? (
      <Button variant="ghost" css={buttonStyle} onClick={() => onUserClick(rowIndex)} data-testid="user-details-button">
        {_t('Details')}
      </Button>
    ) : (
      <React.Fragment />
    );
  };

export const renderBoolean = (bool: boolean | null | undefined) => {
  if (typeof bool === 'boolean') {
    return bool ? _t('Yes') : _t('No');
  } else {
    return '-';
  }
};

export const renderLearnerEffort = (learnerEffort: { learningHours: number; progress: number }) => {
  return (
    <Grid container direction="column">
      <FormattedMessage
        message={_t(
          `{learningHours} {learningHours, plural,
          =1 {hour spent} =0 {0 hours spent} other {hours spent}}`
        )}
        learningHours={(learnerEffort.learningHours ?? 0).toFixed(2)}
      />
      {_t('#{progressPercentage}% completed', { progressPercentage: learnerEffort.progress ?? 0 })}
    </Grid>
  );
};

export const renderStandardCell = (content?: string) => content || '-';

export const renderBusinessUnitCell = ({
  businessUnit,
  businessUnit2,
}: {
  businessUnit?: string;
  businessUnit2?: string;
}) => <Typography2 component="p">{[businessUnit, businessUnit2].filter((unit) => unit).join(', ') || '-'}</Typography2>;

export const renderManagerCell = ({ managerName, managerEmail }: { managerName?: string; managerEmail?: string }) => (
  <div>
    <Typography2 component="p">
      <PersonallyIdentifiableInfo>{managerName ?? '-'}</PersonallyIdentifiableInfo>
    </Typography2>
    <Typography2 component="p" color="supportText" style={{ fontSize: 12 }}>
      <PersonallyIdentifiableInfo>{managerEmail ?? ''}</PersonallyIdentifiableInfo>
    </Typography2>
  </div>
);

export const renderLocationCell = ({
  locationCountry,
  locationRegion,
  locationCity,
}: {
  locationCountry?: string;
  locationRegion?: string;
  locationCity?: string;
}) => (
  <Typography2 component="p">
    {[locationCity, locationRegion, locationCountry].filter((location) => location).join(', ') || '-'}
  </Typography2>
);

export const renderNumberCell = (content?: string) => <p className="c-numberCell m-b-0">{content || '0'}</p>;

export const renderLeftAlignedNumberCell = (content?: string) => <p className="m-b-0">{content || '0'}</p>;

export const renderPercentCell = (content?: string) => <p className="m-b-0 bold">{`${content}%` || '-'}</p>;

export const renderLearner =
  (onUserClick?: (index: number) => void) =>
  ({ name, email }: { name?: string; email?: string }, row: $TSFixMe, rowIndex: number) =>
    (
      <div className="c-userCell vertical-box">
        {!onUserClick && (
          <Typography2 component="p" data-e2e="user-name">
            <PersonallyIdentifiableInfo>{name || ''}</PersonallyIdentifiableInfo>
          </Typography2>
        )}
        {onUserClick && (
          <Button
            variant="ghost"
            fullWidth
            css={buttonStyle}
            onClick={() => onUserClick(rowIndex)}
            data-e2e="user-name-button"
          >
            <PersonallyIdentifiableInfo skeletonWidth={64}>{name || ''}</PersonallyIdentifiableInfo>
          </Button>
        )}
        <Typography2 component="p" color="supportText" style={{ fontSize: 12 }}>
          <PersonallyIdentifiableInfo>{email || ''}</PersonallyIdentifiableInfo>
        </Typography2>
      </div>
    );

export const renderDate = (timestamp?: number) => {
  return timestamp ? formatDateTimeDisplay(timestamp, MOMENT_FORMATS.MED_DATE_ONLY_DISPLAY) : '-';
};

export const renderDateTime = (timestamp?: string): string => {
  return timestamp ? formatDateTimeDisplay(timestamp, MOMENT_FORMATS.MED_DATE_ONLY_DISPLAY) : '-';
};

export const renderFlags = (riskFlagsCount: number) => (
  <div>
    {riskFlagsCount} <SvgFlagSquareFilled size={20} color={riskFlagsCount === 0 ? color.darkGray : color.error} />
  </div>
);

export const renderMultipleEntries = (entries: Array<string>) => <ArrayToStringCell entries={entries} />;

export const renderStatus = (status: ActivityStatus | null) => {
  switch (status) {
    case ActivityStatus.active:
      return <p className="c-statusCell active">{_t('Active')}</p>;
    case ActivityStatus.inactive:
      return <p className="c-statusCell">{_t('Inactive')}</p>;
    case ActivityStatus.completed:
    default:
      return '';
  }
};
